import { groupBy } from 'es-toolkit';
import React, { useMemo } from 'react';

import { calculateBearingEnvelopeShape, calculateFloorPolyline2DShape } from '@paragon/layout';
import { notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../../../components/hooks';
import {
  getAllBearingEnvelopesDictionary,
  getVisibleBearingEnvelopes,
  getVisibleFeaturePatternContainersDictionary,
} from '../../../../store/selectors/layoutDataSelectors';
import { AutomaticRunDimensions } from '../AutomaticRunDimensions';
import { useTrussEnvelopesWithInsertionEnvelope } from '../useTrussEnvelopesWithInsertionEnvelope';

export function FeaturePatternRunDimensionsController() {
  const trussEnvelopes = useTrussEnvelopesWithInsertionEnvelope();

  const allBearingEnvelopes = useAppSelector(getAllBearingEnvelopesDictionary);
  const visibleBearingEnvelopes = useAppSelector(getVisibleBearingEnvelopes);

  const bearingEnvelopeShapes = useMemo(
    () => visibleBearingEnvelopes.map(calculateBearingEnvelopeShape).filter(notNullish),
    [visibleBearingEnvelopes],
  );

  const featurePatternContainers = useAppSelector(getVisibleFeaturePatternContainersDictionary);

  // memoized for stable envelope arrays for automatic run dimension caching
  const featurePatternEnvelopesByContainer = useMemo(
    () =>
      groupBy(
        trussEnvelopes.filter((envelope) => envelope.definitionType === 'FeaturePattern'),
        (envelope) => envelope.featurePatternContainerGuid ?? '',
      ),
    [trussEnvelopes],
  );

  const containerPolygons = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(featurePatternContainers).map(([guid, container]) => [
          guid,
          calculateFloorPolyline2DShape(container.shape, allBearingEnvelopes),
        ]),
      ),
    [featurePatternContainers, allBearingEnvelopes],
  );

  return Object.values(featurePatternContainers)
    .filter(
      (container) =>
        containerPolygons[container.guid] != null && featurePatternEnvelopesByContainer[container.guid] != null,
    )
    .map((container) => (
      <AutomaticRunDimensions
        key={container.guid}
        elevation={container.shape.bottomElevation}
        dimensionedArea={containerPolygons[container.guid]}
        prohibitedAreas={bearingEnvelopeShapes}
        trussEnvelopes={featurePatternEnvelopesByContainer[container.guid]!}
      />
    ));
}
